<template>
  <ModalBase
    :show-modal="showModal"
    :full-h="true"
    :narrow="true"
    :overlay-scrollb="false"
    class="mobile-app-prompt-modal"
    @close-modal="close('close_modal')"
  >
    <template v-slot:maincontent>
      <div class="mobile-app-prompt-modal__content">
        <h2>{{ $gettext('Get the full Gekko experience in the app') }}</h2>
        <p>
          {{ $gettext('For the best experience on your phone, open Gekko in our mobile app instead of the browser.') }}
        </p>

        <div class="mobile-app-prompt-modal__actions">
          <GekkoButton
            v-for="button in storeButtons"
            :key="button.href"
            :href="button.href"
            :icon="button.icon"
            :text="button.text"
            variant="light"
            class="w-100"
            size="lg"
            target="_blank"
            @click="handleStoreClick(button)"
          />

          <GekkoButton
            :text="$gettext('Continue in browser')"
            class="w-100 p-0"
            variant="link"
            @click="close('continue_in_browser')"
          />
        </div>

        <div class="mobile-app-prompt-modal__hero-viewport">
          <img class="mobile-app-prompt-modal__hero" src="/static/img/mobile-app-prompt.png" alt="Gekko mobile app" />
        </div>
      </div>
    </template>
  </ModalBase>
</template>

<script>
import { mapGetters } from 'vuex'

import ModalBase from '@/components/base_components/ModalBase'
import GekkoButton from '@/components/GekkoButton'

const MOBILE_APP_PROMPT_COOKIE = 'gk-mobile-app-prompt-dismissed-v1'

export default {
  name: 'MobileAppPromptModal',
  components: {
    ModalBase,
    GekkoButton,
  },
  data() {
    return {
      detectedPlatform: null,
      hasTrackedPromptShown: false,
      showModal: false,
      storeLinks: {
        ios: 'https://apps.apple.com/app/id1348683531?mt=8',
        android: 'market://details?id=com.getgekko.accounting',
      },
    }
  },
  computed: {
    ...mapGetters({
      activeModal: 'modals/getModal',
      getMobileAppView: 'main/getMobileAppView',
    }),
    isEmbeddedMobileAppView() {
      return this.getMobileAppView === true || this.getMobileAppView === 'true' || this.getMobileAppView === 1 || this.getMobileAppView === '1'
    },
    storeButtons() {
      if (this.detectedPlatform === 'ios') {
        return [
          {
            href: this.storeLinks.ios,
            icon: 'fa-brands fa-apple',
            text: this.$gettext('Open in the App Store'),
          },
        ]
      }

      if (this.detectedPlatform === 'android') {
        return [
          {
            href: this.storeLinks.android,
            icon: 'fa-brands fa-google-play',
            text: this.$gettext('Open in Google Play'),
          },
        ]
      }

      if (this.detectedPlatform === 'generic') {
        return [
          {
            href: this.storeLinks.ios,
            icon: 'fa-brands fa-apple',
            text: this.$gettext('Open in the App Store'),
          },
          {
            href: this.storeLinks.android,
            icon: 'fa-brands fa-google-play',
            text: this.$gettext('Open in Google Play'),
          },
        ]
      }

      return []
    },
  },
  watch: {
    activeModal: {
      immediate: true,
      handler() {
        this.syncVisibility()
      },
    },
    showModal(value) {
      document.body.classList.toggle('modal-on', value)
    },
  },
  mounted() {
    this.detectedPlatform = this.detectMobilePlatform()
    this.syncVisibility()
  },
  destroyed() {
    document.body.classList.remove('modal-on')
  },
  methods: {
    syncVisibility() {
      const shouldShow = this.shouldShowModal()
      if (this.showModal !== shouldShow) {
        this.showModal = shouldShow

        if (shouldShow && !this.hasTrackedPromptShown) {
          this.trackMixpanelEvent('Mobile app prompt: shown')
          this.hasTrackedPromptShown = true
        }
      }
    },
    shouldShowModal() {
      if (!this.detectedPlatform || this.isEmbeddedMobileAppView || this.isDismissed()) {
        return false
      }

      return !this.activeModal || this.activeModal === this.$options.name
    },
    detectMobilePlatform() {
      const userAgent = navigator.userAgent || navigator.vendor || window.opera || ''
      const maxTouchPoints = navigator.maxTouchPoints || 0
      const hasSmallViewport = window.innerWidth < 768
      const isiPad = /iPad/i.test(userAgent) || (/Macintosh/i.test(userAgent) && maxTouchPoints > 1)
      const isAndroidTablet = /Android/i.test(userAgent) && !/Mobile/i.test(userAgent)
      const isTablet = isiPad || isAndroidTablet || /Tablet|PlayBook|Silk/i.test(userAgent)
      const isIPhone = /iPhone|iPod/i.test(userAgent)
      const isAndroidPhone = /Android/i.test(userAgent) && /Mobile/i.test(userAgent)

      if (isTablet) {
        return null
      }

      if (isIPhone) {
        return 'ios'
      }

      if (isAndroidPhone) {
        return 'android'
      }

      if (navigator.userAgentData && navigator.userAgentData.mobile && hasSmallViewport) {
        return 'generic'
      }

      if (hasSmallViewport && maxTouchPoints > 0) {
        return 'generic'
      }

      return null
    },
    isDismissed() {
      return this.$cookies.get(MOBILE_APP_PROMPT_COOKIE) === '1'
    },
    persistDismissal() {
      const now = new Date()
      const expire = new Date(now)

      expire.setMonth(now.getMonth() + 6)
      this.$cookies.set(MOBILE_APP_PROMPT_COOKIE, '1', expire, '/')
    },
    handleStoreClick(button) {
      const source = button && button.href === this.storeLinks.ios
        ? 'app_store'
        : button && button.href === this.storeLinks.android
          ? 'google_play'
          : 'unknown'

      this.trackMixpanelEvent('Mobile app prompt: open store', {
        source,
        destination: button && button.href ? button.href : null,
      })
      this.persistDismissal()
      this.showModal = false
    },
    close(source = 'dismissed') {
      this.trackMixpanelEvent('Mobile app prompt: dismissed', {
        source,
      })
      this.persistDismissal()
      this.showModal = false
    },
    trackMixpanelEvent(eventName, payload = {}) {
      if (!window.mixpanel) {
        return
      }

      window.mixpanel.track(eventName, {
        platform: this.detectedPlatform,
        ...payload,
      })
    },
  },
}
</script>

<style scoped lang="scss">
.mobile-app-prompt-modal {
  z-index: 10000;
  &__content {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    text-align: center;
    min-height: 100%;
    gap: 1rem;
    padding: 60px 0.25rem 0.25rem;
    color: #ffffff;

    h2 {
      font-size: 1.75rem;
      line-height: 1.1;
      margin: 0;
    }

    p {
      color: #ffffff;
      margin: 0;
    }
  }

  &__hero {
    width: 100%;
    max-width: 320px;
    height: auto;
    margin: 0;
  }

  &__hero-viewport {
    width: 100%;
    max-width: 320px;
    min-height: 150px;
    flex: 1 1 auto;
    margin-top: 0.75rem;
    display: flex;
    justify-content: center;
    align-items: flex-start;
    overflow: hidden;
  }

  &__note {
    font-size: 0.95rem;
    opacity: 0.9;
  }

  &__actions {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
    margin-top: 0.5rem;
  }
}
</style>

<style lang="scss">
.mobile-app-prompt-modal {
  .gk-modal-wrapper {
    padding: 0;
  }

  .gk-modal {
    border-radius: 0;
    background: $colorBrand url('/static/img/tile_background-brand.png') repeat;
    color: #ffffff;
  }

  .btn-link {
    color: #ffffff;
    opacity: 0.9;
  }

  .btn-link:hover,
  .btn-link:focus {
    color: #ffffff;
    opacity: 1;
  }

  .close-btn {
    top: 0 !important;
    right: 0 !important;
    background: transparent !important;
  }
}
</style>